/**
 * Project Lifecycle Manager
 * 
 * Manages project phase transitions with proper state machine logic,
 * guards, and validations. Handles the complete project lifecycle:
 * 
 * Planning → Development → Testing → Deployment → Production → End → R&D
 */

import { blink } from '@/lib/blink';
import { safeJsonParse } from '@/lib/utils';

export enum ProjectPhase {
  PLANNING = 'planning',
  DEVELOPMENT = 'development',
  TESTING = 'testing',
  DEPLOYMENT = 'deployment',
  PRODUCTION = 'production',
  MAINTENANCE = 'maintenance',
  END = 'end',
  R_AND_D = 'r_and_d',
  ARCHIVE = 'archive'
}

export interface ProjectLifecycleState {
  id: string;
  projectId: string;
  userId: string;
  currentPhase: ProjectPhase;
  previousPhase?: ProjectPhase;
  transitionedAt: Date;
  reason?: string;
  metadata?: Record<string, any>;
  completionPercentage: number;
  blockers?: string[];
  nextExpectedPhase?: ProjectPhase;
  estimatedDurationDays?: number;
  actualDurationDays?: number;
  createdAt: Date;
  updatedAt: Date;
}

export interface TransitionGuardResult {
  allowed: boolean;
  reason?: string;
  requiredActions?: string[];
  warnings?: string[];
}

const VALID_TRANSITIONS: Record<ProjectPhase, ProjectPhase[]> = {
  [ProjectPhase.PLANNING]: [ProjectPhase.DEVELOPMENT],
  [ProjectPhase.DEVELOPMENT]: [ProjectPhase.TESTING],
  [ProjectPhase.TESTING]: [ProjectPhase.DEPLOYMENT, ProjectPhase.DEVELOPMENT],
  [ProjectPhase.DEPLOYMENT]: [ProjectPhase.PRODUCTION, ProjectPhase.TESTING],
  [ProjectPhase.PRODUCTION]: [ProjectPhase.MAINTENANCE, ProjectPhase.END],
  [ProjectPhase.MAINTENANCE]: [ProjectPhase.END, ProjectPhase.PRODUCTION],
  [ProjectPhase.END]: [ProjectPhase.R_AND_D, ProjectPhase.ARCHIVE],
  [ProjectPhase.R_AND_D]: [ProjectPhase.DEVELOPMENT, ProjectPhase.ARCHIVE],
  [ProjectPhase.ARCHIVE]: []
};

export class ProjectLifecycleManager {
  static async initializeProject(
    projectId: string,
    userId: string,
    metadata?: Record<string, any>
  ): Promise<ProjectLifecycleState> {
    const now = new Date();
    
    const state: ProjectLifecycleState = {
      id: `pls_${Date.now()}_${Math.random().toString(36).substring(7)}`,
      projectId,
      userId,
      currentPhase: ProjectPhase.PLANNING,
      transitionedAt: now,
      completionPercentage: 0,
      metadata: {
        initialMetadata: metadata,
        startedAt: now.toISOString()
      },
      createdAt: now,
      updatedAt: now
    };

    await blink.db.projectLifecycleStates.create({
      id: state.id,
      projectId,
      userId,
      currentPhase: state.currentPhase,
      transitionedAt: state.transitionedAt.toISOString(),
      completionPercentage: state.completionPercentage,
      metadata: JSON.stringify(state.metadata),
      createdAt: state.createdAt.toISOString(),
      updatedAt: state.updatedAt.toISOString()
    });

    console.log(`✅ Project initialized in PLANNING phase: ${projectId}`);
    return state;
  }

  static async getCurrentState(projectId: string): Promise<ProjectLifecycleState | null> {
    try {
      const states = await blink.db.projectLifecycleStates.list({
        where: { projectId },
        orderBy: { transitionedAt: 'desc' },
        limit: 1
      });

      if (!states || states.length === 0) {
        return null;
      }

      const record = states[0];
      return {
        id: record.id,
        projectId: record.projectId,
        userId: record.userId,
        currentPhase: record.currentPhase as ProjectPhase,
        previousPhase: record.previousPhase as ProjectPhase | undefined,
        transitionedAt: new Date(record.transitionedAt),
        reason: record.reason,
        completionPercentage: record.completionPercentage,
        blockers: record.blockers ? safeJsonParse(record.blockers) : undefined,
        nextExpectedPhase: record.nextExpectedPhase as ProjectPhase | undefined,
        estimatedDurationDays: record.estimatedDurationDays,
        actualDurationDays: record.actualDurationDays,
        metadata: record.metadata ? safeJsonParse(record.metadata) : undefined,
        createdAt: new Date(record.createdAt),
        updatedAt: new Date(record.updatedAt)
      };
    } catch (error) {
      console.error('Failed to get current state:', error);
      return null;
    }
  }

  static async getProjectHistory(projectId: string): Promise<ProjectLifecycleState[]> {
    try {
      const states = await blink.db.projectLifecycleStates.list({
        where: { projectId },
        orderBy: { transitionedAt: 'asc' }
      });

      return (states || []).map(record => ({
        id: record.id,
        projectId: record.projectId,
        userId: record.userId,
        currentPhase: record.currentPhase as ProjectPhase,
        previousPhase: record.previousPhase as ProjectPhase | undefined,
        transitionedAt: new Date(record.transitionedAt),
        reason: record.reason,
        completionPercentage: record.completionPercentage,
        blockers: record.blockers ? safeJsonParse(record.blockers) : undefined,
        metadata: record.metadata ? safeJsonParse(record.metadata) : undefined,
        createdAt: new Date(record.createdAt),
        updatedAt: new Date(record.updatedAt)
      }));
    } catch (error) {
      console.error('Failed to get project history:', error);
      return [];
    }
  }

  static async validateTransition(
    projectId: string,
    toPhase: ProjectPhase
  ): Promise<TransitionGuardResult> {
    const currentState = await this.getCurrentState(projectId);

    if (!currentState) {
      return {
        allowed: false,
        reason: 'Project not found or not initialized'
      };
    }

    const fromPhase = currentState.currentPhase;
    const validNextPhases = VALID_TRANSITIONS[fromPhase];
    
    if (!validNextPhases || !validNextPhases.includes(toPhase)) {
      return {
        allowed: false,
        reason: `Invalid transition: cannot go from ${fromPhase} to ${toPhase}`
      };
    }

    return {
      allowed: true,
      warnings: []
    };
  }

  static async transitionPhase(
    projectId: string,
    userId: string,
    toPhase: ProjectPhase,
    reason: string,
    metadata?: Record<string, any>
  ): Promise<{ success: boolean; state?: ProjectLifecycleState; error?: string }> {
    const validation = await this.validateTransition(projectId, toPhase);
    
    if (!validation.allowed) {
      return {
        success: false,
        error: validation.reason || 'Transition not allowed'
      };
    }

    const currentState = await this.getCurrentState(projectId);
    if (!currentState) {
      return {
        success: false,
        error: 'Project not found'
      };
    }

    const fromPhase = currentState.currentPhase;
    const now = new Date();

    const newState: ProjectLifecycleState = {
      id: `pls_${Date.now()}_${Math.random().toString(36).substring(7)}`,
      projectId,
      userId,
      currentPhase: toPhase,
      previousPhase: fromPhase,
      transitionedAt: now,
      reason,
      completionPercentage: currentState.completionPercentage,
      metadata: {
        ...currentState.metadata,
        ...metadata,
        transitionReason: reason,
        transitionedBy: userId,
        fromPhase
      },
      createdAt: now,
      updatedAt: now
    };

    await blink.db.projectLifecycleStates.create({
      id: newState.id,
      projectId: newState.projectId,
      userId: newState.userId,
      currentPhase: newState.currentPhase,
      previousPhase: newState.previousPhase,
      transitionedAt: newState.transitionedAt.toISOString(),
      reason: newState.reason,
      completionPercentage: newState.completionPercentage,
      metadata: JSON.stringify(newState.metadata),
      createdAt: newState.createdAt.toISOString(),
      updatedAt: newState.updatedAt.toISOString()
    });

    console.log(`✅ Project transitioned: ${fromPhase} → ${toPhase} (${projectId})`);

    return {
      success: true,
      state: newState
    };
  }

  static async endProjectAndStartRND(
    projectId: string,
    userId: string,
    options: {
      rndFocus: string;
      researchDurationWeeks: number;
      lessonsLearned?: string[];
      projectAnalysis?: string;
      rndPlan?: string;
    }
  ): Promise<{ success: boolean; state?: ProjectLifecycleState; error?: string }> {
    const currentState = await this.getCurrentState(projectId);
    if (!currentState) {
      return { success: false, error: 'Project not found' };
    }

    const validStartPhases = [ProjectPhase.PRODUCTION, ProjectPhase.MAINTENANCE];
    if (!validStartPhases.includes(currentState.currentPhase)) {
      return {
        success: false,
        error: `Cannot end project in ${currentState.currentPhase} phase. Must be in PRODUCTION or MAINTENANCE.`
      };
    }

    if (currentState.currentPhase !== ProjectPhase.END) {
      const endTransition = await this.transitionPhase(
        projectId,
        userId,
        ProjectPhase.END,
        'Project ending - preparing for R&D phase',
        {
          dataArchived: true,
          usersNotified: true,
          supportPlanFinalized: true
        }
      );

      if (!endTransition.success) {
        return {
          success: false,
          error: `Failed to transition to END phase: ${endTransition.error}`
        };
      }
    }

    const rndTransition = await this.transitionPhase(
      projectId,
      userId,
      ProjectPhase.R_AND_D,
      'Transitioning from project end to research and development phase',
      {
        rndFocus: options.rndFocus,
        researchDurationWeeks: options.researchDurationWeeks,
        lessonsLearned: options.lessonsLearned || [],
        projectAnalysis: options.projectAnalysis,
        rndPlan: options.rndPlan,
        projectEndDate: new Date().toISOString(),
        rndStartDate: new Date().toISOString()
      }
    );

    if (!rndTransition.success) {
      return {
        success: false,
        error: `Failed to transition to R&D phase: ${rndTransition.error}`
      };
    }

    console.log(`🔬 Project successfully transitioned to R&D phase: ${projectId}`);
    return {
      success: true,
      state: rndTransition.state
    };
  }

  static async completeRNDAndRestartDevelopment(
    projectId: string,
    userId: string,
    options: {
      rndFindings: string[];
      developmentPlan: string;
      newFeatures?: string[];
      estimatedDurationWeeks?: number;
    }
  ): Promise<{ success: boolean; state?: ProjectLifecycleState; error?: string }> {
    const currentState = await this.getCurrentState(projectId);
    if (!currentState || currentState.currentPhase !== ProjectPhase.R_AND_D) {
      return {
        success: false,
        error: 'Project must be in R&D phase to complete R&D and restart development'
      };
    }

    const transition = await this.transitionPhase(
      projectId,
      userId,
      ProjectPhase.DEVELOPMENT,
      'R&D phase completed - restarting development with findings',
      {
        rndFindingsApproved: true,
        rndFindings: options.rndFindings,
        developmentPlan: options.developmentPlan,
        newFeatures: options.newFeatures,
        rndCompletedAt: new Date().toISOString()
      }
    );

    if (!transition.success) {
      return {
        success: false,
        error: `Failed to transition from R&D to Development: ${transition.error}`
      };
    }

    console.log(`✅ R&D phase completed, development restarted: ${projectId}`);
    return {
      success: true,
      state: transition.state
    };
  }

  static async getPhaseAnalytics(projectId: string): Promise<{
    currentPhase: ProjectPhase;
    totalDuration: number;
    phaseBreakdown: Array<{
      phase: ProjectPhase;
      duration: number;
      startDate: Date;
      endDate?: Date;
      reason?: string;
    }>;
    completionPercentage: number;
    estimatedCompletion?: Date;
  }> {
    const history = await this.getProjectHistory(projectId);
    const current = await this.getCurrentState(projectId);

    if (!current) {
      throw new Error('Project not found');
    }

    const phaseBreakdown = [];
    let totalDuration = 0;

    for (let i = 0; i < history.length; i++) {
      const state = history[i];
      const nextState = history[i + 1];
      const endDate = nextState ? nextState.transitionedAt : undefined;
      const duration = endDate 
        ? Math.floor((endDate.getTime() - state.transitionedAt.getTime()) / (1000 * 60 * 60 * 24))
        : Math.floor((new Date().getTime() - state.transitionedAt.getTime()) / (1000 * 60 * 60 * 24));

      totalDuration += duration;

      phaseBreakdown.push({
        phase: state.currentPhase,
        duration,
        startDate: state.transitionedAt,
        endDate,
        reason: state.reason
      });
    }

    return {
      currentPhase: current.currentPhase,
      totalDuration,
      phaseBreakdown,
      completionPercentage: current.completionPercentage
    };
  }

  static async updateCompletion(
    projectId: string,
    completionPercentage: number,
    userId: string
  ): Promise<boolean> {
    const current = await this.getCurrentState(projectId);
    if (!current) return false;

    await blink.db.projectLifecycleStates.create({
      id: `pls_${Date.now()}_${Math.random().toString(36).substring(7)}`,
      projectId,
      userId,
      currentPhase: current.currentPhase,
      previousPhase: current.previousPhase,
      transitionedAt: current.transitionedAt.toISOString(),
      completionPercentage,
      metadata: JSON.stringify({
        ...current.metadata,
        completionUpdatedAt: new Date().toISOString()
      }),
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    });

    return true;
  }

  static async addBlockers(
    projectId: string,
    userId: string,
    blockers: string[]
  ): Promise<boolean> {
    const current = await this.getCurrentState(projectId);
    if (!current) return false;

    const existingBlockers = current.blockers || [];
    const allBlockers = [...new Set([...existingBlockers, ...blockers])];

    await blink.db.projectLifecycleStates.create({
      id: `pls_${Date.now()}_${Math.random().toString(36).substring(7)}`,
      projectId,
      userId,
      currentPhase: current.currentPhase,
      previousPhase: current.previousPhase,
      transitionedAt: current.transitionedAt.toISOString(),
      completionPercentage: current.completionPercentage,
      blockers: JSON.stringify(allBlockers),
      metadata: JSON.stringify(current.metadata),
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    });

    return true;
  }
}
